import { Share } from "@capacitor/share";
import { isNativePlatform } from "./platform";
import { copyToClipboard } from "./native-share";

export type ShareResult = "files" | "url" | "copied" | "unsupported";

const sanitize = (s: string) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const extFromMime = (mime: string) => (mime.split("/")[1] || "jpg").split("+")[0];

const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("read failed"));
    reader.onloadend = () => {
      const result = String(reader.result || "");
      resolve(result.includes(",") ? result.split(",")[1] : result);
    };
    reader.readAsDataURL(blob);
  });

const fetchBlob = async (url: string): Promise<Blob | null> => {
  try {
    const res = await fetch(url, { mode: "cors" });
    if (!res.ok) return null;
    const blob = await res.blob();
    return blob.size > 0 ? blob : null;
  } catch {
    return null;
  }
};

interface ShareWithImagesOpts {
  /** Public image URLs to attach. First one is the primary. */
  images: string[];
  /** Canonical link to include alongside the image. */
  url: string;
  title?: string;
  text?: string;
  /** Base used for generated filenames, e.g. "modern-living-room". */
  filenameBase?: string;
}

/**
 * Share one or more images together with the canonical link.
 *
 * Native (iOS/Android): downloads the images into the app cache and hands the
 * real files to the OS share sheet so social apps receive the picture.
 * Web: attaches File objects via the Web Share API (link appended to the text
 * so targets that ignore `url` still carry it).
 *
 * Falls back to a URL-only share, then to copying the link.
 */
export const shareWithImages = async ({
  images,
  url,
  title,
  text,
  filenameBase = "decor-canvas",
}: ShareWithImagesOpts): Promise<ShareResult> => {
  const base = sanitize(filenameBase) || "decor-canvas";
  const list = images.filter(Boolean);
  const message = text ? `${text}\n${url}` : url;

  if (isNativePlatform()) {
    const paths: string[] = [];
    let Filesystem: typeof import("@capacitor/filesystem").Filesystem | null = null;
    let Directory: typeof import("@capacitor/filesystem").Directory | null = null;
    try {
      const fs = await import("@capacitor/filesystem");
      Filesystem = fs.Filesystem;
      Directory = fs.Directory;
    } catch {
      Filesystem = null;
    }

    if (Filesystem && Directory) {
      for (let i = 0; i < list.length; i++) {
        const blob = await fetchBlob(list[i]);
        if (!blob) continue;
        try {
          const data = await blobToBase64(blob);
          const suffix = list.length > 1 ? `-${i + 1}` : "";
          const path = `${base}${suffix}-${Date.now()}.${extFromMime(blob.type)}`;
          await Filesystem.writeFile({ path, data, directory: Directory.Cache });
          const { uri } = await Filesystem.getUri({ path, directory: Directory.Cache });
          paths.push(uri);
        } catch (err) {
          console.warn("[shareWithImages] failed to stage image for native share", err);
        }
      }
    }

    try {
      if (paths.length > 0) {
        await Share.share({ title, text: message, files: paths, dialogTitle: "Share" });
        return "files";
      }
      await Share.share({ title, text, url, dialogTitle: "Share" });
      return "url";
    } catch {
      // user cancelled — treat as handled
      return paths.length > 0 ? "files" : "url";
    } finally {
      if (Filesystem && Directory && paths.length > 0) {
        for (const uri of paths) {
          const name = uri.split("/").pop();
          if (!name) continue;
          void Filesystem.deleteFile({ path: name, directory: Directory.Cache }).catch(() => {});
        }
      }
    }
  }

  // ---- Web ----
  if (typeof navigator !== "undefined" && navigator.share) {
    const files: File[] = [];
    for (let i = 0; i < list.length; i++) {
      const blob = await fetchBlob(list[i]);
      if (!blob) continue;
      const suffix = list.length > 1 ? `-${i + 1}` : "";
      files.push(
        new File([blob], `${base}${suffix}.${extFromMime(blob.type)}`, { type: blob.type }),
      );
    }

    try {
      if (files.length > 0 && navigator.canShare?.({ files })) {
        // `url` is intentionally omitted: several targets drop the files when
        // both are present. The link lives inside the text instead.
        await navigator.share({ files, title, text: message });
        return "files";
      }
    } catch (err: unknown) {
      if ((err as { name?: string })?.name === "AbortError") return "files";
      // fall through to URL-only
    }

    try {
      await navigator.share({ title, text, url });
      return "url";
    } catch (err: unknown) {
      if ((err as { name?: string })?.name === "AbortError") return "url";
    }
  }

  const ok = await copyToClipboard(url);
  return ok ? "copied" : "unsupported";
};
